import { zod } from "@lya-protect/lya-protect-form-library/dist/Exports";
import {
  Form,
  InputSelect,
  useFormConfig,
} from "@lya-protect/lya-protect-form-library/dist/FormAPI";
import { InfoCard } from "@src/components/infoCard/InfoCard";
import { LoaderOverlay } from "@src/components/loaderOverlay/LoaderOverlay";
import { Stepbar } from "@src/components/stepbar/Stepbar";
import { MicroStep, StepProps } from "@src/Steps/step/Steps";
import { ChangeEvent, useCallback, useMemo, useState } from "react";
import { PJDocument, ProjetSignature } from "@src/App";
import axios from "axios";
import {
  Button,
  Emphasis,
  FormAction,
  FormField,
  FormGrid,
  FormNotice,
  FormRow,
  FormSubTitle,
  FormTitle,
} from "@lya-protect/lya-protect-form-library/dist/UI";
import { displayToast } from "@src/components/toaster/Toaster";
import { captureException } from "@sentry/react";
import "./piecesJustificatives.scss";

enum TypePieceIdentite {
  CNI = "CNI",
  PASSEPORT = "PASSEPORT",
  PERMIS = "PERMIS",
  TITRE_SEJOUR = "TITRE_SEJOUR",
}

type LocalPJ = {
  numero: string;
  description: string;
  document?: PJDocument;
};

type IdentityDocuments = {
  recto?: PJDocument;
  verso?: PJDocument;
};

const schema = zod.object({
  typePieceIdentite: zod.nativeEnum(TypePieceIdentite),
});

type FormValues = zod.infer<typeof schema>;

const typePieceIdentiteOptions = [
  { label: "Carte d'identité", value: TypePieceIdentite.CNI },
  { label: "Passeport", value: TypePieceIdentite.PASSEPORT },
  { label: "Permis de conduire", value: TypePieceIdentite.PERMIS },
  {
    label: "Titre de séjour en cours de validité",
    value: TypePieceIdentite.TITRE_SEJOUR,
  },
];

const PJDescriptions = [
  "Pièce d'identité Recto",
  "Pièce d'identité Verso",
  "RIB",
  "Justificatif de domicile",
  "Acte de naissance",
];

const PJDetailedDescriptions: Record<string, string> = {
  "Pièce d'identité Recto":
    "Une copie recto de la carte d'identité, du passeport, du permis de conduire ou du titre de séjour en cours de validité",
  "Pièce d'identité Verso":
    "Une copie verso de la carte d'identité, du passeport, du permis de conduire ou du titre de séjour en cours de validité",
  RIB: "Un relevé d'identité bancaire (RIB) comportant les mentions IBAN - BIC",
  "Justificatif de domicile":
    "Un justificatif de domicile de moins de 3 mois (facture d'eau, électricité, téléphone, gaz, quittance de loyer ou d'assurance, avis d'imposition ou de non-imposition)",
  "Acte de naissance":
    "Une copie intégrale de l'acte de naissance ou une copie du livret de famille complet (en cas d'adhésion d'un mineur)",
};

const ACCEPTED_FILE_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "image/jpeg",
  "image/png",
];

const MAX_FILE_SIZE_MB = 20;

export type PiecesJustificativesData = {
  hasSubmitted?: boolean;
  hasResolved?: boolean;
};
type PiecesJustificativesProps = StepProps<PiecesJustificativesData>;

export type InputPJProps = {
  numero: string;
  document?: PJDocument;
  disabled?: boolean;
  title: string;
  description: string;
  onUpload: (document: PJDocument) => void;
  onDelete: () => void;
};

function InputPJ({
  numero,
  title,
  description,
  disabled,
  document,
  onDelete,
  onUpload,
}: InputPJProps) {
  const [status, setStatus] = useState<
    "MISSING" | "SUBMITTING" | "DELETING" | "FAILED" | "SUCCESS"
  >(document ? "SUCCESS" : "MISSING");

  async function handleSubmit(file: File): Promise<PJDocument> {
    if (!ACCEPTED_FILE_TYPES.includes(file.type)) {
      displayToast(
        "Format non pris en charge.\n Extensions acceptées : .jpg, .png, .pdf, .doc, .docx.",
        "error"
      );
      throw new Error("Invalid file type");
    }

    const fd = new FormData();
    fd.append("fileUploadedName", file.name);
    fd.append("fileContent", file);

    try {
      const res = await axios.post<PJDocument>(
        `//${process.env.VITE_URL_GATEWAY}/document/${numero}`,
        fd,
        {
          headers: {
            "Content-type": "multipart/form-data",
          },
        }
      );
      return res.data;
    } catch (err) {
      captureException(err);
      console.error(err);

      if ((err as { response?: { status: number } }).response?.status === 413) {
        displayToast(
          `Le fichier est trop volumineux (max ${MAX_FILE_SIZE_MB}Mo)`,
          "error"
        );
      } else {
        displayToast(
          "Une erreur est survenue lors de l'envoi du fichier",
          "error"
        );
      }
      throw err;
    }
  }

  function handleDelete() {
    setStatus("DELETING");
    onDelete();
    setStatus("MISSING");
  }

  function handleChange(e: ChangeEvent<HTMLInputElement>) {
    if (!e.target.files || !e.target.files[0]) return;

    setStatus("SUBMITTING");
    handleSubmit(e.target.files[0])
      .then((res) => {
        setStatus("SUCCESS");
        onUpload(res);
      })
      .catch(() => {
        setStatus("FAILED");
      });
  }

  if (status === "SUCCESS" || status === "DELETING") {
    return (
      <div className="pieces-justificatives-step__input-file-success">
        <a target="__blank" href={document?.url}>
          {document?.fileUploadedName}
        </a>
        <button
          onClick={handleDelete}
          disabled={disabled || status === "DELETING"}
          aria-label="Supprimer le document"
        >
          {status === "SUCCESS" && (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              fill="var(--accent-color)"
              className="bi bi-trash"
              viewBox="0 0 16 16"
            >
              <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z" />
              <path
                fillRule="evenodd"
                d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"
              />
            </svg>
          )}
          {status === "DELETING" && (
            <svg
              className="pieces-justificatives-step__input-file-delete-loader"
              version="1.1"
              xmlns="http://www.w3.org/2000/svg"
              x="0px"
              y="0px"
              viewBox="0 0 100 100"
              enableBackground="new 0 0 0 0"
              xmlSpace="preserve"
            >
              <path
                fill="var(--accent-color)"
                d="M73,50c0-12.7-10.3-23-23-23S27,37.3,27,50 M30.9,50c0-10.5,8.5-19.1,19.1-19.1S69.1,39.5,69.1,50"
              >
                <animateTransform
                  attributeName="transform"
                  attributeType="XML"
                  type="rotate"
                  dur="1s"
                  from="0 50 50"
                  to="360 50 50"
                  repeatCount="indefinite"
                />
              </path>
            </svg>
          )}
        </button>
      </div>
    );
  }

  return (
    <div
      className="pieces-justificatives-step__input-file"
      data-status={status}
    >
      <input
        disabled={disabled || status === "SUBMITTING"}
        type="file"
        accept={ACCEPTED_FILE_TYPES.join(", ")}
        name={numero}
        id={numero}
        onChange={handleChange}
      />
      <label htmlFor={numero}>
        {status === "MISSING" && (
          <div>
            <span>{title}</span>
            <p>{description}</p>
          </div>
        )}
        {status === "SUBMITTING" && (
          <>
            <svg
              version="1.1"
              xmlns="http://www.w3.org/2000/svg"
              x="0px"
              y="0px"
              viewBox="0 0 100 100"
              enableBackground="new 0 0 0 0"
              xmlSpace="preserve"
            >
              <path
                fill="var(--accent-color)"
                d="M73,50c0-12.7-10.3-23-23-23S27,37.3,27,50 M30.9,50c0-10.5,8.5-19.1,19.1-19.1S69.1,39.5,69.1,50"
              >
                <animateTransform
                  attributeName="transform"
                  attributeType="XML"
                  type="rotate"
                  dur="1s"
                  from="0 50 50"
                  to="360 50 50"
                  repeatCount="indefinite"
                />
              </path>
            </svg>
            <div>
              <span>{title}</span>
              <p>{description}</p>
            </div>
          </>
        )}
        {status === "FAILED" && (
          <div>
            <span>{title}</span>
            <p>{description}</p>
          </div>
        )}
      </label>
    </div>
  );
}

export function PiecesJustificatives({
  projectId,
  changeStep,
  projetSignature,
  setProjetSignature,
  setData,
}: PiecesJustificativesProps) {
  const formConfig = useFormConfig<typeof schema, FormValues>({
    schema,
    defaultValues: {
      typePieceIdentite: TypePieceIdentite.CNI,
    },
  });

  const typePieceIdentite = formConfig.watch("typePieceIdentite");

  const [identityDocuments, _] = useState<IdentityDocuments>({});

  const [localPJ, setLocalPJ] = useState(() =>
    (projetSignature as ProjetSignature).piecesJustificatives
      .filter((pj) => {
        if (pj.description === "Acte de naissance") {
          if (!projetSignature?.assure?.birthDate) return false;
          const birthDate = new Date(projetSignature.assure.birthDate);
          const today = new Date();
          const age = today.getFullYear() - birthDate.getFullYear();
          const monthDiff = today.getMonth() - birthDate.getMonth();
          const dayDiff = today.getDate() - birthDate.getDate();

          const actualAge =
            monthDiff < 0 || (monthDiff === 0 && dayDiff < 0) ? age - 1 : age;
          return actualAge < 18;
        }
        return true;
      })
      .sort((pjA, pjB) => {
        const ia = PJDescriptions.indexOf(pjA.description);
        const ib = PJDescriptions.indexOf(pjB.description);
        return ia - ib;
      })
      .reduce<Record<string, LocalPJ>>((acc, curr) => {
        acc[curr.numero] = {
          numero: curr.numero,
          description: curr.description,
          document: curr.document,
        };
        return acc;
      }, {})
  );

  const { identityPJ, otherPJ } = useMemo(() => {
    const pjList = Object.values(localPJ);

    const rectoDoc = pjList.find((pj) =>
      pj.description.toLowerCase().includes("recto")
    );
    const versoDoc = pjList.find((pj) =>
      pj.description.toLowerCase().includes("verso")
    );

    const otherDocs = pjList.filter(
      (pj) =>
        !pj.description.toLowerCase().includes("recto") &&
        !pj.description.toLowerCase().includes("verso")
    );

    if (!typePieceIdentite) {
      return {
        identityPJ: [],
        otherPJ: otherDocs,
      };
    }

    if (typePieceIdentite === TypePieceIdentite.PASSEPORT) {
      return {
        identityPJ: rectoDoc ? [rectoDoc] : [],
        otherPJ: otherDocs,
      };
    }

    const identityDocs = [];
    if (rectoDoc) identityDocs.push(rectoDoc);
    if (versoDoc) identityDocs.push(versoDoc);

    return {
      identityPJ: identityDocs,
      otherPJ: otherDocs,
    };
  }, [localPJ, typePieceIdentite]);

  const handleUpload = useCallback((numero: string, document: PJDocument) => {
    setLocalPJ((state) => ({
      ...state,
      [numero]: {
        ...state[numero],
        document,
      },
    }));
  }, []);

  const handleDelete = useCallback((numero: string) => {
    setLocalPJ((state) => ({
      ...state,
      [numero]: {
        ...state[numero],
        document: undefined,
      },
    }));
  }, []);

  const stepIsValid = useMemo(() => {
    if (!typePieceIdentite) return false;

    const identityDocsValid = identityPJ.every((pj) => pj.document);
    const otherDocsValid = otherPJ.every((pj) => pj.document);

    return identityDocsValid && otherDocsValid;
  }, [typePieceIdentite, identityPJ, otherPJ]);

  async function sign(): Promise<void> {
    if (!projectId) throw new Error("Project ID is missing");

    await axios.put<unknown>(
      `//${process.env.VITE_URL_GATEWAY}/signer/${projectId}`,
      {
        mailAssure: projetSignature?.assure.mail,
      }
    );
  }

  const handleSubmit = useCallback(async () => {
    try {
      setData({ hasSubmitted: true });

      if (!projetSignature) {
        return;
      }

      const updatedPiecesJustificatives =
        projetSignature.piecesJustificatives.map((pj) => {
          return {
            ...pj,
            document: localPJ[pj.numero]?.document,
          };
        });

      setProjetSignature({
        ...projetSignature,
        piecesJustificatives: updatedPiecesJustificatives,
      });

      await sign();

      setData({
        hasSubmitted: true,
        hasResolved: true,
      });

      changeStep("RECAP_SIGNATURE", "forward");
    } catch (err) {
      setData({
        hasSubmitted: true,
        hasResolved: false,
      });
      captureException(err);
      console.error(err);
      displayToast("Une erreur est survenue lors de la signature", "error");
    }
  }, [
    identityDocuments,
    localPJ,
    projetSignature,
    projectId,
    setData,
    setProjetSignature,
    changeStep,
  ]);

  if (!localPJ || !projetSignature) return null;

  return (
    <>
      <LoaderOverlay isLoading={formConfig.formState.isSubmitting} />
      <div className="pieces-justificatives-step">
        <div className="stepper__centered-container">
          <div className="stepper__centered-content step__grid step__grid--padding-bottom pieces-justificatives-step__main">
            <Stepbar activeMicroStep={MicroStep.SupportingDocuments} />
            <Form
              className="pieces-justificatives-step__form"
              formConfig={formConfig}
              onSubmit={handleSubmit}
            >
              <FormGrid>
                <FormTitle>
                  <h1>
                    Complétez <Emphasis>votre dossier d&apos;adhésion</Emphasis>
                  </h1>
                </FormTitle>
                <FormNotice>
                  <p>
                    <Emphasis>*</Emphasis> Champs obligatoires
                  </p>
                </FormNotice>
                <FormSubTitle>
                  <h2>Votre pièce d&apos;identité</h2>
                </FormSubTitle>
                <FormRow>
                  <FormField takeFullSizeMobile size="half">
                    <InputSelect
                      name="typePieceIdentite"
                      options={typePieceIdentiteOptions}
                      label="Type de votre pièce d'identité"
                      placeholder="Sélectionner le type"
                      required
                    />
                  </FormField>
                </FormRow>

                <FormRow>
                  <FormField>
                    {!typePieceIdentite ? (
                      <InfoCard>
                        <p>
                          Veuillez d&apos;abord sélectionner le type de votre
                          pièce d&apos;identité
                        </p>
                      </InfoCard>
                    ) : (
                      <div className="pieces-justificatives-step__input-list">
                        {identityPJ.map((PJ) => (
                          <InputPJ
                            key={PJ.numero}
                            document={PJ.document}
                            numero={PJ.numero}
                            title={PJ.description}
                            description={
                              PJDetailedDescriptions[PJ.description] || ""
                            }
                            onDelete={() => handleDelete(PJ.numero)}
                            onUpload={(document) =>
                              handleUpload(PJ.numero, document)
                            }
                          />
                        ))}
                      </div>
                    )}
                  </FormField>
                </FormRow>
                <FormNotice>
                  <h2>Autres documents nécessaires</h2>
                </FormNotice>

                <div className="pieces-justificatives-step__input-list">
                  {otherPJ.map((PJ) => (
                    <InputPJ
                      key={PJ.numero}
                      document={PJ.document}
                      numero={PJ.numero}
                      title={PJ.description}
                      description={PJDetailedDescriptions[PJ.description] || ""}
                      onDelete={() => handleDelete(PJ.numero)}
                      onUpload={(document) => handleUpload(PJ.numero, document)}
                    />
                  ))}
                </div>

                <FormAction>
                  <Button disabled={!stepIsValid} type="submit">
                    Suivant
                  </Button>
                </FormAction>
              </FormGrid>
            </Form>
          </div>
        </div>
      </div>
    </>
  );
}
